import React, { useState } from 'react';
import { CheckboxInput, ExpandableCard, InputGroup, OpeningHoursInput, Pill } from '@nearst/ui';
import PriceMarkupInput from '../PriceMarkupInput';
import ResetCategoryMarkdownModal from '../ResetCategoryMarkdownModal';
import { compareSchedules } from './utils';
import styles from './DeliveryMenuSettings.module.scss';

const ExpandableCategory = ({ category, channel, authorized, loading, updateCategory }) => {
	const defaultDeliveryMarkup =
		channel.config?.categoryOverrides?.[category.title]?.deliveryMarkup || channel.config?.defaultDeliveryMarkup;

	const [showOpeningHours, setShowOpeningHours] = useState(!!channel.config?.categoryOverrides?.[category.title]?.schedule);

	/**
	 * Checks if a category has custom overrides that differ from the channel defaults
	 * Returns true if either:
	 * 1. The delivery markup is set and different from the channel default
	 * 2. The schedule is set and different from the channel default schedule
	 */
	const hasCategoryOverrides = () => {
		const overrides = channel.config?.categoryOverrides?.[category.title];
		if (!overrides) return false;

		const hasDeliveryMarkup =
			overrides.deliveryMarkup !== undefined && overrides.deliveryMarkup !== channel.config?.defaultDeliveryMarkup;
		const hasSchedule = overrides.schedule !== undefined && !compareSchedules(overrides.schedule, channel.config?.schedule);

		return hasDeliveryMarkup || hasSchedule;
	};

	return (
		<ExpandableCard.Root key={category.title} value={category.title}>
			<ExpandableCard.Title sideTitle={`${category.itemCount?.toLocaleString()} items`}>
				<>
					{category.title}
					{hasCategoryOverrides() && <Pill colour="grey">Edited</Pill>}
				</>
			</ExpandableCard.Title>
			<ExpandableCard.Content>
				<div className={styles.inputRow}>
					<PriceMarkupInput
						id={`${category.title}-defaultDeliveryMarkup`}
						label="Default price markup"
						value={defaultDeliveryMarkup}
						disabled={!authorized || loading}
						onUpdate={(target, value) =>
							updateCategory(target, 'deliveryMarkup', value, channel.config?.defaultDeliveryMarkup, category.title)
						}
					/>
				</div>

				<ResetCategoryMarkdownModal channel={channel.type} categoryTitle={category.title} deliveryMarkup={defaultDeliveryMarkup} />

				<div>
					<InputGroup htmlFor={`customOpeningHours-${category.title}`}>
						<CheckboxInput
							id={`customOpeningHours-${category.title}`}
							label={`Custom opening hours`}
							labelClassName={styles.checkboxInput}
							checked={showOpeningHours}
							onChange={(e) => {
								setShowOpeningHours(e.target.checked);
								if (!e.target.checked) {
									updateCategory({}, 'schedule', undefined, channel.config?.schedule, category.title);
								}
							}}
						/>
					</InputGroup>
					{showOpeningHours && (
						<div className={styles.openingHoursInput}>
							<OpeningHoursInput
								value={channel.config?.categoryOverrides?.[category.title]?.schedule || channel.config?.schedule}
								onChange={(value) => {
									updateCategory({}, 'schedule', value, channel.config?.schedule, category.title);
								}}
							/>
						</div>
					)}
				</div>
			</ExpandableCard.Content>
		</ExpandableCard.Root>
	);
};

export default ExpandableCategory;
